/**
 * ASIC Products Configuration
 * Based on ASIC RG 271 - Internal dispute resolution reporting
 * https://asic.gov.au/regulatory-resources/find-a-document/regulatory-guides/rg-271-internal-dispute-resolution-data-reporting/
 */

type AsicProductNodeBase = {
    label: string;
};

export type AsicProductLevel3 = AsicProductNodeBase & {
    code: number;
    fullLabel: string;
    level: 3;
};

export type AsicProductLevel2 = AsicProductNodeBase & {
    id: string;
    level: 2;
    children: AsicProductLevel3[];
};

export type AsicProductLevel1 = AsicProductNodeBase & {
    id: string;
    level: 1;
    children: AsicProductLevel2[];
};

export type AsicProductTreeNode = AsicProductLevel1 | AsicProductLevel2 | AsicProductLevel3;

/**
 * Flattened product structure for easy lookup and filtering
 * Used for display in forms and product selection
 */
export type AsicProductFlat = {
    code: number;
    label: string;
    fullLabel: string;
    category1: string | null;
    category2: string | null;
};

/**
 * Organisation-level visibility configuration for ASIC products
 * Controls which products appear in dropdowns for complaints/incidents
 */
export type AsicProductVisibilityConfig = {
    hidden: number[];
    visible: number[];
};

/**
 * Hierarchical tree structure for UI navigation (cascader)
 */
export const ASIC_PRODUCTS_TREE: (AsicProductLevel1 | AsicProductLevel3)[] = [
    // Special case: No parent categories
    {
        code: 188,
        label: 'Not product/service-related',
        fullLabel: '188 - Not product/service-related',
        level: 3,
    },
    // General insurance
    {
        id: 'general-insurance',
        label: 'General insurance',
        level: 1,
        children: [
            {
                id: 'domestic-insurance',
                label: 'Domestic insurance',
                level: 2,
                children: [
                    {
                        code: 40,
                        label: 'Consumer credit insurance',
                        fullLabel: '40 - Consumer credit insurance',
                        level: 3,
                    },
                    {
                        code: 41,
                        label: 'Home building',
                        fullLabel: '41 - Home building',
                        level: 3,
                    },
                    {
                        code: 42,
                        label: 'Home contents',
                        fullLabel: '42 - Home contents',
                        level: 3,
                    },
                    {
                        code: 43,
                        label: 'Landlord insurance',
                        fullLabel: '43 - Landlord insurance',
                        level: 3,
                    },
                    {
                        code: 44,
                        label: 'Motor vehicle—Comprehensive',
                        fullLabel: '44 - Motor vehicle—Comprehensive',
                        level: 3,
                    },
                    {
                        code: 45,
                        label: 'Motor vehicle—Third-party (fire and theft)',
                        fullLabel: '45 - Motor vehicle—Third-party (fire and theft)',
                        level: 3,
                    },
                    {
                        code: 46,
                        label: 'Motor vehicle—Third-party',
                        fullLabel: '46 - Motor vehicle—Third-party',
                        level: 3,
                    },
                    {
                        code: 47,
                        label: 'Motor vehicle—Uninsured third-party',
                        fullLabel: '47 - Motor vehicle—Uninsured third-party',
                        level: 3,
                    },
                    {
                        code: 48,
                        label: 'Personal and domestic property—Mobile phone',
                        fullLabel: '48 - Personal and domestic property—Mobile phone',
                        level: 3,
                    },
                    {
                        code: 49,
                        label: 'Personal and domestic property—Domestic pet/horse',
                        fullLabel: '49 - Personal and domestic property—Domestic pet/horse',
                        level: 3,
                    },
                    {
                        code: 50,
                        label: 'Personal and domestic property—Caravan/trailer',
                        fullLabel: '50 - Personal and domestic property—Caravan/trailer',
                        level: 3,
                    },
                    {
                        code: 51,
                        label: 'Personal and domestic property—Pleasure craft',
                        fullLabel: '51 - Personal and domestic property—Pleasure craft',
                        level: 3,
                    },
                    {
                        code: 52,
                        label: 'Personal and domestic property—Valuables/other moveable property',
                        fullLabel: '52 - Personal and domestic property—Valuables/other moveable property',
                        level: 3,
                    },
                    {
                        code: 53,
                        label: 'Residential strata title',
                        fullLabel: '53 - Residential strata title',
                        level: 3,
                    },
                    {
                        code: 54,
                        label: 'Sickness and accident insurance',
                        fullLabel: '54 - Sickness and accident insurance',
                        level: 3,
                    },
                    {
                        code: 55,
                        label: 'Ticket insurance',
                        fullLabel: '55 - Ticket insurance',
                        level: 3,
                    },
                    {
                        code: 56,
                        label: 'Travel insurance',
                        fullLabel: '56 - Travel insurance',
                        level: 3,
                    },
                    {
                        code: 57,
                        label: 'Trust bond',
                        fullLabel: '57 - Trust bond',
                        level: 3,
                    },
                    {
                        code: 58,
                        label: 'Other domestic insurance',
                        fullLabel: '58 - Other domestic insurance',
                        level: 3,
                    },
                ],
            },
            {
                id: 'extended-warranty',
                label: 'Extended warranty',
                level: 2,
                children: [
                    {
                        code: 59,
                        label: 'Brown goods',
                        fullLabel: '59 - Brown goods',
                        level: 3,
                    },
                    {
                        code: 60,
                        label: 'Motor vehicles',
                        fullLabel: '60 - Motor vehicles',
                        level: 3,
                    },
                    {
                        code: 61,
                        label: 'White goods',
                        fullLabel: '61 - White goods',
                        level: 3,
                    },
                    {
                        code: 62,
                        label: 'Other extended warranty',
                        fullLabel: '62 - Other extended warranty',
                        level: 3,
                    },
                ],
            },
            {
                id: 'professional-indemnity-insurance',
                label: 'Professional indemnity insurance',
                level: 2,
                children: [
                    {
                        code: 63,
                        label: 'Medical indemnity insurance',
                        fullLabel: '63 - Medical indemnity insurance',
                        level: 3,
                    },
                    {
                        code: 64,
                        label: 'Other professional indemnity',
                        fullLabel: '64 - Other professional indemnity',
                        level: 3,
                    },
                ],
            },
            {
                id: 'small-business-farm-insurance',
                label: 'Small business/farm insurance',
                level: 2,
                children: [
                    {
                        code: 65,
                        label: 'Commercial property',
                        fullLabel: '65 - Commercial property',
                        level: 3,
                    },
                    {
                        code: 66,
                        label: 'Commercial vehicle',
                        fullLabel: '66 - Commercial vehicle',
                        level: 3,
                    },
                    {
                        code: 67,
                        label: 'Computer and electronic breakdown',
                        fullLabel: '67 - Computer and electronic breakdown',
                        level: 3,
                    },
                    {
                        code: 68,
                        label: 'Contractors all risk',
                        fullLabel: '68 - Contractors all risk',
                        level: 3,
                    },
                    {
                        code: 69,
                        label: 'Fire or accident damage',
                        fullLabel: '69 - Fire or accident damage',
                        level: 3,
                    },
                    {
                        code: 70,
                        label: 'Glass',
                        fullLabel: '70 - Glass',
                        level: 3,
                    },
                    {
                        code: 71,
                        label: 'Industrial special risk',
                        fullLabel: '71 - Industrial special risk',
                        level: 3,
                    },
                    {
                        code: 72,
                        label: 'Land transit',
                        fullLabel: '72 - Land transit',
                        level: 3,
                    },
                    {
                        code: 73,
                        label: 'Livestock',
                        fullLabel: '73 - Livestock',
                        level: 3,
                    },
                    {
                        code: 74,
                        label: 'Loss of profits/business interruption',
                        fullLabel: '74 - Loss of profits/business interruption',
                        level: 3,
                    },
                    {
                        code: 75,
                        label: 'Machinery breakdowns',
                        fullLabel: '75 - Machinery breakdowns',
                        level: 3,
                    },
                    {
                        code: 76,
                        label: 'Money',
                        fullLabel: '76 - Money',
                        level: 3,
                    },
                    {
                        code: 77,
                        label: 'Public liability',
                        fullLabel: '77 - Public liability',
                        level: 3,
                    },
                    {
                        code: 78,
                        label: 'Thefts',
                        fullLabel: '78 - Thefts',
                        level: 3,
                    },
                    {
                        code: 79,
                        label: 'Other small business/farm insurance',
                        fullLabel: '79 - Other small business/farm insurance',
                        level: 3,
                    },
                ],
            },
        ],
    },
    // Life insurance
    {
        id: 'life-insurance',
        label: 'Life insurance',
        level: 1,
        children: [
            {
                id: 'life-insurance-products',
                label: 'Life insurance',
                level: 2,
                children: [
                    {
                        code: 114,
                        label: 'Consumer credit insurance',
                        fullLabel: '114 - Consumer credit insurance',
                        level: 3,
                    },
                    {
                        code: 115,
                        label: 'Income protection',
                        fullLabel: '115 - Income protection',
                        level: 3,
                    },
                    {
                        code: 116,
                        label: 'Annuities',
                        fullLabel: '116 - Annuities',
                        level: 3,
                    },
                    {
                        code: 117,
                        label: 'Endowments',
                        fullLabel: '117 - Endowments',
                        level: 3,
                    },
                    {
                        code: 118,
                        label: 'Funeral insurance/funeral plans',
                        fullLabel: '118 - Funeral insurance/funeral plans',
                        level: 3,
                    },
                    {
                        code: 119,
                        label: 'Scholarship funds',
                        fullLabel: '119 - Scholarship funds',
                        level: 3,
                    },
                    {
                        code: 120,
                        label: 'Team life',
                        fullLabel: '120 - Team life',
                        level: 3,
                    },
                    {
                        code: 121,
                        label: 'Terminal illness',
                        fullLabel: '121 - Terminal illness',
                        level: 3,
                    },
                    {
                        code: 122,
                        label: 'Total and permanent disability',
                        fullLabel: '122 - Total and permanent disability',
                        level: 3,
                    },
                    {
                        code: 123,
                        label: 'Trauma',
                        fullLabel: '123 - Trauma',
                        level: 3,
                    },
                    {
                        code: 124,
                        label: 'Whole of life',
                        fullLabel: '124 - Whole of life',
                        level: 3,
                    },
                    {
                        code: 125,
                        label: 'Other life insurance',
                        fullLabel: '125 - Other life insurance',
                        level: 3,
                    },
                ],
            },
        ],
    },
    // Superannuation
    {
        id: 'superannuation',
        label: 'Superannuation',
        level: 1,
        children: [
            {
                id: 'annuity-policy',
                label: 'Annuity policy',
                level: 2,
                children: [
                    {
                        code: 143,
                        label: 'Pension',
                        fullLabel: '143 - Pension',
                        level: 3,
                    },
                    {
                        code: 144,
                        label: 'Death benefit',
                        fullLabel: '144 - Death benefit',
                        level: 3,
                    },
                    {
                        code: 145,
                        label: 'Other features within an annuity policy',
                        fullLabel: '145 - Other features within an annuity policy',
                        level: 3,
                    },
                ],
            },
            {
                id: 'approved-deposit-fund',
                label: 'Approved deposit fund',
                level: 2,
                children: [
                    {
                        code: 146,
                        label: 'Pension',
                        fullLabel: '146 - Pension',
                        level: 3,
                    },
                    {
                        code: 147,
                        label: 'Death benefit',
                        fullLabel: '147 - Death benefit',
                        level: 3,
                    },
                    {
                        code: 148,
                        label: 'Terminal illness',
                        fullLabel: '148 - Terminal illness',
                        level: 3,
                    },
                    {
                        code: 149,
                        label: 'Total and permanent disability',
                        fullLabel: '149 - Total and permanent disability',
                        level: 3,
                    },
                    {
                        code: 150,
                        label: 'Income protection',
                        fullLabel: '150 - Income protection',
                        level: 3,
                    },
                    {
                        code: 151,
                        label: 'Superannuation account',
                        fullLabel: '151 - Superannuation account',
                        level: 3,
                    },
                    {
                        code: 152,
                        label: 'Other features within an approved deposit fund',
                        fullLabel: '152 - Other features within an approved deposit fund',
                        level: 3,
                    },
                ],
            },
            {
                id: 'life-policy-fund',
                label: 'Life policy fund',
                level: 2,
                children: [
                    {
                        code: 153,
                        label: 'Death benefit',
                        fullLabel: '153 - Death benefit',
                        level: 3,
                    },
                    {
                        code: 154,
                        label: 'Terminal illness',
                        fullLabel: '154 - Terminal illness',
                        level: 3,
                    },
                    {
                        code: 155,
                        label: 'Total and permanent disability',
                        fullLabel: '155 - Total and permanent disability',
                        level: 3,
                    },
                    {
                        code: 156,
                        label: 'Income protection',
                        fullLabel: '156 - Income protection',
                        level: 3,
                    },
                    {
                        code: 157,
                        label: 'Superannuation account',
                        fullLabel: '157 - Superannuation account',
                        level: 3,
                    },
                    {
                        code: 158,
                        label: 'Other features within a life policy fund',
                        fullLabel: '158 - Other features within a life policy fund',
                        level: 3,
                    },
                ],
            },
            {
                id: 'retirement-savings-account',
                label: 'Retirement savings account',
                level: 2,
                children: [
                    {
                        code: 159,
                        label: 'Death benefit',
                        fullLabel: '159 - Death benefit',
                        level: 3,
                    },
                    {
                        code: 160,
                        label: 'Terminal illness',
                        fullLabel: '160 - Terminal illness',
                        level: 3,
                    },
                    {
                        code: 161,
                        label: 'Total and permanent disability',
                        fullLabel: '161 - Total and permanent disability',
                        level: 3,
                    },
                    {
                        code: 162,
                        label: 'Income protection',
                        fullLabel: '162 - Income protection',
                        level: 3,
                    },
                    {
                        code: 163,
                        label: 'RSA Account',
                        fullLabel: '163 - RSA Account',
                        level: 3,
                    },
                    {
                        code: 164,
                        label: 'Other features within a retirement savings account',
                        fullLabel: '164 - Other features within a retirement savings account',
                        level: 3,
                    },
                ],
            },
            {
                id: 'small-apra-fund',
                label: 'Small APRA fund',
                level: 2,
                children: [
                    {
                        code: 165,
                        label: 'Pension',
                        fullLabel: '165 - Pension',
                        level: 3,
                    },
                    {
                        code: 166,
                        label: 'Death benefit',
                        fullLabel: '166 - Death benefit',
                        level: 3,
                    },
                    {
                        code: 167,
                        label: 'Terminal illness',
                        fullLabel: '167 - Terminal illness',
                        level: 3,
                    },
                    {
                        code: 168,
                        label: 'Total and permanent disability',
                        fullLabel: '168 - Total and permanent disability',
                        level: 3,
                    },
                    {
                        code: 169,
                        label: 'Income protection',
                        fullLabel: '169 - Income protection',
                        level: 3,
                    },
                    {
                        code: 170,
                        label: 'Superannuation account',
                        fullLabel: '170 - Superannuation account',
                        level: 3,
                    },
                    {
                        code: 171,
                        label: 'Other features within a small APRA fund',
                        fullLabel: '171 - Other features within a small APRA fund',
                        level: 3,
                    },
                ],
            },
            {
                id: 'superannuation-fund',
                label: 'Superannuation fund',
                level: 2,
                children: [
                    {
                        code: 172,
                        label: 'Pension',
                        fullLabel: '172 - Pension',
                        level: 3,
                    },
                    {
                        code: 173,
                        label: 'Death benefit',
                        fullLabel: '173 - Death benefit',
                        level: 3,
                    },
                    {
                        code: 174,
                        label: 'Terminal illness',
                        fullLabel: '174 - Terminal illness',
                        level: 3,
                    },
                    {
                        code: 175,
                        label: 'Total and permanent disability',
                        fullLabel: '175 - Total and permanent disability',
                        level: 3,
                    },
                    {
                        code: 176,
                        label: 'Income protection',
                        fullLabel: '176 - Income protection',
                        level: 3,
                    },
                    {
                        code: 177,
                        label: 'Superannuation account',
                        fullLabel: '177 - Superannuation account',
                        level: 3,
                    },
                    {
                        code: 178,
                        label: 'Other features within a superannuation fund',
                        fullLabel: '178 - Other features within a superannuation fund',
                        level: 3,
                    },
                ],
            },
        ],
    },
    // Financial Advice or Credit Assistance
    {
        id: 'financial-advice-credit-assistance',
        label: 'Financial Advice or Credit Assistance',
        level: 1,
        children: [
            {
                id: 'financial-advice',
                label: 'Financial advice',
                level: 2,
                children: [
                    {
                        code: 185,
                        label: 'General advice',
                        fullLabel: '185 - General advice',
                        level: 3,
                    },
                    {
                        code: 186,
                        label: 'Personal advice',
                        fullLabel: '186 - Personal advice',
                        level: 3,
                    },
                ],
            },
        ],
    },
    // Discretionary risk products
    {
        id: 'discretionary-risk-products',
        label: 'Discretionary risk products',
        level: 1,
        children: [
            {
                id: 'mutual-risk-products',
                label: 'Mutual risk products (MRPs)',
                level: 2,
                children: [
                    {
                        code: 189,
                        label: 'Mutual risk products',
                        fullLabel: '189 - Mutual risk products',
                        level: 3,
                    },
                ],
            },
        ],
    },
];

/**
 * Flat array of all ASIC products for easy lookup and filtering
 * Total: 97 products
 */
export const ASIC_PRODUCTS: AsicProductFlat[] = [
    // Special case
    {
        code: 188,
        label: 'Not product/service-related',
        fullLabel: '188 - Not product/service-related',
        category1: null,
        category2: null,
    },
    // General insurance > Domestic insurance
    {
        code: 40,
        label: 'Consumer credit insurance',
        fullLabel: '40 - Consumer credit insurance',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 41,
        label: 'Home building',
        fullLabel: '41 - Home building',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 42,
        label: 'Home contents',
        fullLabel: '42 - Home contents',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 43,
        label: 'Landlord insurance',
        fullLabel: '43 - Landlord insurance',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 44,
        label: 'Motor vehicle—Comprehensive',
        fullLabel: '44 - Motor vehicle—Comprehensive',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 45,
        label: 'Motor vehicle—Third-party (fire and theft)',
        fullLabel: '45 - Motor vehicle—Third-party (fire and theft)',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 46,
        label: 'Motor vehicle—Third-party',
        fullLabel: '46 - Motor vehicle—Third-party',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 47,
        label: 'Motor vehicle—Uninsured third-party',
        fullLabel: '47 - Motor vehicle—Uninsured third-party',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 48,
        label: 'Personal and domestic property—Mobile phone',
        fullLabel: '48 - Personal and domestic property—Mobile phone',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 49,
        label: 'Personal and domestic property—Domestic pet/horse',
        fullLabel: '49 - Personal and domestic property—Domestic pet/horse',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 50,
        label: 'Personal and domestic property—Caravan/trailer',
        fullLabel: '50 - Personal and domestic property—Caravan/trailer',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 51,
        label: 'Personal and domestic property—Pleasure craft',
        fullLabel: '51 - Personal and domestic property—Pleasure craft',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 52,
        label: 'Personal and domestic property—Valuables/other moveable property',
        fullLabel: '52 - Personal and domestic property—Valuables/other moveable property',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 53,
        label: 'Residential strata title',
        fullLabel: '53 - Residential strata title',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 54,
        label: 'Sickness and accident insurance',
        fullLabel: '54 - Sickness and accident insurance',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 55,
        label: 'Ticket insurance',
        fullLabel: '55 - Ticket insurance',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 56,
        label: 'Travel insurance',
        fullLabel: '56 - Travel insurance',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 57,
        label: 'Trust bond',
        fullLabel: '57 - Trust bond',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    {
        code: 58,
        label: 'Other domestic insurance',
        fullLabel: '58 - Other domestic insurance',
        category1: 'General insurance',
        category2: 'Domestic insurance',
    },
    // General insurance > Extended warranty
    {
        code: 59,
        label: 'Brown goods',
        fullLabel: '59 - Brown goods',
        category1: 'General insurance',
        category2: 'Extended warranty',
    },
    {
        code: 60,
        label: 'Motor vehicles',
        fullLabel: '60 - Motor vehicles',
        category1: 'General insurance',
        category2: 'Extended warranty',
    },
    {
        code: 61,
        label: 'White goods',
        fullLabel: '61 - White goods',
        category1: 'General insurance',
        category2: 'Extended warranty',
    },
    {
        code: 62,
        label: 'Other extended warranty',
        fullLabel: '62 - Other extended warranty',
        category1: 'General insurance',
        category2: 'Extended warranty',
    },
    // General insurance > Professional indemnity insurance
    {
        code: 63,
        label: 'Medical indemnity insurance',
        fullLabel: '63 - Medical indemnity insurance',
        category1: 'General insurance',
        category2: 'Professional indemnity insurance',
    },
    {
        code: 64,
        label: 'Other professional indemnity',
        fullLabel: '64 - Other professional indemnity',
        category1: 'General insurance',
        category2: 'Professional indemnity insurance',
    },
    // General insurance > Small business/farm insurance
    {
        code: 65,
        label: 'Commercial property',
        fullLabel: '65 - Commercial property',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 66,
        label: 'Commercial vehicle',
        fullLabel: '66 - Commercial vehicle',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 67,
        label: 'Computer and electronic breakdown',
        fullLabel: '67 - Computer and electronic breakdown',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 68,
        label: 'Contractors all risk',
        fullLabel: '68 - Contractors all risk',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 69,
        label: 'Fire or accident damage',
        fullLabel: '69 - Fire or accident damage',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 70,
        label: 'Glass',
        fullLabel: '70 - Glass',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 71,
        label: 'Industrial special risk',
        fullLabel: '71 - Industrial special risk',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 72,
        label: 'Land transit',
        fullLabel: '72 - Land transit',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 73,
        label: 'Livestock',
        fullLabel: '73 - Livestock',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 74,
        label: 'Loss of profits/business interruption',
        fullLabel: '74 - Loss of profits/business interruption',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 75,
        label: 'Machinery breakdowns',
        fullLabel: '75 - Machinery breakdowns',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 76,
        label: 'Money',
        fullLabel: '76 - Money',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 77,
        label: 'Public liability',
        fullLabel: '77 - Public liability',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 78,
        label: 'Thefts',
        fullLabel: '78 - Thefts',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    {
        code: 79,
        label: 'Other small business/farm insurance',
        fullLabel: '79 - Other small business/farm insurance',
        category1: 'General insurance',
        category2: 'Small business/farm insurance',
    },
    // Life insurance > Life insurance
    {
        code: 114,
        label: 'Consumer credit insurance',
        fullLabel: '114 - Consumer credit insurance',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 115,
        label: 'Income protection',
        fullLabel: '115 - Income protection',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 116,
        label: 'Annuities',
        fullLabel: '116 - Annuities',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 117,
        label: 'Endowments',
        fullLabel: '117 - Endowments',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 118,
        label: 'Funeral insurance/funeral plans',
        fullLabel: '118 - Funeral insurance/funeral plans',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 119,
        label: 'Scholarship funds',
        fullLabel: '119 - Scholarship funds',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 120,
        label: 'Team life',
        fullLabel: '120 - Team life',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 121,
        label: 'Terminal illness',
        fullLabel: '121 - Terminal illness',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 122,
        label: 'Total and permanent disability',
        fullLabel: '122 - Total and permanent disability',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 123,
        label: 'Trauma',
        fullLabel: '123 - Trauma',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 124,
        label: 'Whole of life',
        fullLabel: '124 - Whole of life',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    {
        code: 125,
        label: 'Other life insurance',
        fullLabel: '125 - Other life insurance',
        category1: 'Life insurance',
        category2: 'Life insurance',
    },
    // Superannuation > Annuity policy
    {
        code: 143,
        label: 'Pension',
        fullLabel: '143 - Pension',
        category1: 'Superannuation',
        category2: 'Annuity policy',
    },
    {
        code: 144,
        label: 'Death benefit',
        fullLabel: '144 - Death benefit',
        category1: 'Superannuation',
        category2: 'Annuity policy',
    },
    {
        code: 145,
        label: 'Other features within an annuity policy',
        fullLabel: '145 - Other features within an annuity policy',
        category1: 'Superannuation',
        category2: 'Annuity policy',
    },
    // Superannuation > Approved deposit fund
    {
        code: 146,
        label: 'Pension',
        fullLabel: '146 - Pension',
        category1: 'Superannuation',
        category2: 'Approved deposit fund',
    },
    {
        code: 147,
        label: 'Death benefit',
        fullLabel: '147 - Death benefit',
        category1: 'Superannuation',
        category2: 'Approved deposit fund',
    },
    {
        code: 148,
        label: 'Terminal illness',
        fullLabel: '148 - Terminal illness',
        category1: 'Superannuation',
        category2: 'Approved deposit fund',
    },
    {
        code: 149,
        label: 'Total and permanent disability',
        fullLabel: '149 - Total and permanent disability',
        category1: 'Superannuation',
        category2: 'Approved deposit fund',
    },
    {
        code: 150,
        label: 'Income protection',
        fullLabel: '150 - Income protection',
        category1: 'Superannuation',
        category2: 'Approved deposit fund',
    },
    {
        code: 151,
        label: 'Superannuation account',
        fullLabel: '151 - Superannuation account',
        category1: 'Superannuation',
        category2: 'Approved deposit fund',
    },
    {
        code: 152,
        label: 'Other features within an approved deposit fund',
        fullLabel: '152 - Other features within an approved deposit fund',
        category1: 'Superannuation',
        category2: 'Approved deposit fund',
    },
    // Superannuation > Life policy fund
    {
        code: 153,
        label: 'Death benefit',
        fullLabel: '153 - Death benefit',
        category1: 'Superannuation',
        category2: 'Life policy fund',
    },
    {
        code: 154,
        label: 'Terminal illness',
        fullLabel: '154 - Terminal illness',
        category1: 'Superannuation',
        category2: 'Life policy fund',
    },
    {
        code: 155,
        label: 'Total and permanent disability',
        fullLabel: '155 - Total and permanent disability',
        category1: 'Superannuation',
        category2: 'Life policy fund',
    },
    {
        code: 156,
        label: 'Income protection',
        fullLabel: '156 - Income protection',
        category1: 'Superannuation',
        category2: 'Life policy fund',
    },
    {
        code: 157,
        label: 'Superannuation account',
        fullLabel: '157 - Superannuation account',
        category1: 'Superannuation',
        category2: 'Life policy fund',
    },
    {
        code: 158,
        label: 'Other features within a life policy fund',
        fullLabel: '158 - Other features within a life policy fund',
        category1: 'Superannuation',
        category2: 'Life policy fund',
    },
    // Superannuation > Retirement savings account
    {
        code: 159,
        label: 'Death benefit',
        fullLabel: '159 - Death benefit',
        category1: 'Superannuation',
        category2: 'Retirement savings account',
    },
    {
        code: 160,
        label: 'Terminal illness',
        fullLabel: '160 - Terminal illness',
        category1: 'Superannuation',
        category2: 'Retirement savings account',
    },
    {
        code: 161,
        label: 'Total and permanent disability',
        fullLabel: '161 - Total and permanent disability',
        category1: 'Superannuation',
        category2: 'Retirement savings account',
    },
    {
        code: 162,
        label: 'Income protection',
        fullLabel: '162 - Income protection',
        category1: 'Superannuation',
        category2: 'Retirement savings account',
    },
    {
        code: 163,
        label: 'Superannuation account',
        fullLabel: '163 - Superannuation account',
        category1: 'Superannuation',
        category2: 'Retirement savings account',
    },
    {
        code: 164,
        label: 'Other features within a retirement savings account',
        fullLabel: '164 - Other features within a retirement savings account',
        category1: 'Superannuation',
        category2: 'Retirement savings account',
    },
    // Superannuation > Small APRA fund
    {
        code: 165,
        label: 'Pension',
        fullLabel: '165 - Pension',
        category1: 'Superannuation',
        category2: 'Small APRA fund',
    },
    {
        code: 166,
        label: 'Death benefit',
        fullLabel: '166 - Death benefit',
        category1: 'Superannuation',
        category2: 'Small APRA fund',
    },
    {
        code: 167,
        label: 'Terminal illness',
        fullLabel: '167 - Terminal illness',
        category1: 'Superannuation',
        category2: 'Small APRA fund',
    },
    {
        code: 168,
        label: 'Total and permanent disability',
        fullLabel: '168 - Total and permanent disability',
        category1: 'Superannuation',
        category2: 'Small APRA fund',
    },
    {
        code: 169,
        label: 'Income protection',
        fullLabel: '169 - Income protection',
        category1: 'Superannuation',
        category2: 'Small APRA fund',
    },
    {
        code: 170,
        label: 'Superannuation account',
        fullLabel: '170 - Superannuation account',
        category1: 'Superannuation',
        category2: 'Small APRA fund',
    },
    {
        code: 171,
        label: 'Other features within a small APRA fund',
        fullLabel: '171 - Other features within a small APRA fund',
        category1: 'Superannuation',
        category2: 'Small APRA fund',
    },
    // Superannuation > Superannuation fund
    {
        code: 172,
        label: 'Pension',
        fullLabel: '172 - Pension',
        category1: 'Superannuation',
        category2: 'Superannuation fund',
    },
    {
        code: 173,
        label: 'Death benefit',
        fullLabel: '173 - Death benefit',
        category1: 'Superannuation',
        category2: 'Superannuation fund',
    },
    {
        code: 174,
        label: 'Terminal illness',
        fullLabel: '174 - Terminal illness',
        category1: 'Superannuation',
        category2: 'Superannuation fund',
    },
    {
        code: 175,
        label: 'Total and permanent disability',
        fullLabel: '175 - Total and permanent disability',
        category1: 'Superannuation',
        category2: 'Superannuation fund',
    },
    {
        code: 176,
        label: 'Income protection',
        fullLabel: '176 - Income protection',
        category1: 'Superannuation',
        category2: 'Superannuation fund',
    },
    {
        code: 177,
        label: 'Superannuation account',
        fullLabel: '177 - Superannuation account',
        category1: 'Superannuation',
        category2: 'Superannuation fund',
    },
    {
        code: 178,
        label: 'Other features within a superannuation fund',
        fullLabel: '178 - Other features within a superannuation fund',
        category1: 'Superannuation',
        category2: 'Superannuation fund',
    },
    // Financial Advice or Credit Assistance > Financial advice
    {
        code: 185,
        label: 'General advice',
        fullLabel: '185 - General advice',
        category1: 'Financial Advice or Credit Assistance',
        category2: 'Financial advice',
    },
    {
        code: 186,
        label: 'Personal advice',
        fullLabel: '186 - Personal advice',
        category1: 'Financial Advice or Credit Assistance',
        category2: 'Financial advice',
    },
    // Discretionary risk products > Mutual risk products (MRPs)
    {
        code: 189,
        label: 'Mutual risk products',
        fullLabel: '189 - Mutual risk products',
        category1: 'Discretionary risk products',
        category2: 'Mutual risk products (MRPs)',
    },
];

/**
 * Sentinel value representing "missing product or service" in filter where clauses.
 * 0 is not a valid ASIC product code, so it is safe to use as a marker.
 * The server converts this to Prisma.DbNull for database queries.
 */
export const MISSING_PRODUCT_SENTINEL = 0;

/**
 * Helper function to get product by code
 */
export function getAsicProductByCode(code: number): AsicProductFlat | undefined {
    return ASIC_PRODUCTS.find((p) => p.code === code);
}

/**
 * Helper function to get visible products based on org configuration
 * @param config - Org-specific visibility overrides
 * @param defaultConfig - System-wide default visibility (from database)
 */
export function getVisibleAsicProducts(
    config: AsicProductVisibilityConfig,
    defaultConfig?: AsicProductVisibilityConfig,
): AsicProductFlat[] {
    return ASIC_PRODUCTS.filter((product) => {
        // Explicit override: force hide
        if (config.hidden.includes(product.code)) {
            return false;
        }
        // Explicit override: force show
        if (config.visible.includes(product.code)) {
            return true;
        }
        // Use default visibility from database
        // defaultConfig should always be provided; if not, assume hidden for safety
        return defaultConfig ? defaultConfig.visible.includes(product.code) : false;
    });
}

/**
 * Helper function to filter tree by visible products
 * @param config - Org-specific visibility overrides
 * @param defaultConfig - System-wide default visibility (from database)
 */
export function filterAsicProductsTree(
    config: AsicProductVisibilityConfig,
    defaultConfig?: AsicProductVisibilityConfig,
): (AsicProductLevel1 | AsicProductLevel3)[] {
    const visibleCodes = new Set(getVisibleAsicProducts(config, defaultConfig).map((p) => p.code));

    return ASIC_PRODUCTS_TREE.map((node) => {
        if ('code' in node) {
            // Level 3 product
            return visibleCodes.has(node.code) ? node : null;
        }

        // Level 1 category
        const filteredChildren = node.children
            .map((level2) => {
                const filteredLevel3 = level2.children.filter((level3) => visibleCodes.has(level3.code));

                return filteredLevel3.length > 0 ? { ...level2, children: filteredLevel3 } : null;
            })
            .filter((child): child is AsicProductLevel2 => child !== null);

        return filteredChildren.length > 0 ? { ...node, children: filteredChildren } : null;
    }).filter((node): node is AsicProductLevel1 | AsicProductLevel3 => node !== null);
}
